<template>
  <v-container fluid>
    <v-row dense>
      <v-col cols="12">

        <Dialog
        :stateId="dialogId"
        maxWidth="30em"
        @click:outside="close"
        @dialog-closing="close"
        @close="close">
          <ConfirmDelete
          v-if="confirmDeleteDialog"
          maxWidth="30em"
          @delete="performDelete"
          @cancel-delete="close"
          :title="$t('dueDiligenceExemption')"/>
          <DueDiligenceExemptionForm
          v-else
          :propDueDiligenceExemption="dueDiligenceExemption"
          @due-diligence-exemption-mutated="refresh" />
        </Dialog>

        <v-row>
          <v-col>
            <v-row align="center" no-gutters>
              <v-icon color="primary" class="mr-2">mdi-shield-check-outline</v-icon>
              <span class="text-h6 font-weight-regular">{{ $t('dueDiligenceExemptions') }}</span>
            </v-row>
            <p class="text-caption mb-0">{{ $t('dueDiligenceExemptionsSubtitle') }}</p>
            <DataTable
            class="exemption-table mt-n6"
            :propDense="false"
            :customCells="customColumns"
            :headers="headers"
            :items="allDueDiligenceExemptions"
            :loading="loading">

              <template #description="{ item }">
                <div v-if="item.description" class="constrained-cell">{{ item.description }}</div>
                <span v-else class="font-italic grey--text">{{ $t('noneSpecified') }}</span>
              </template>

              <template #options="{ item }">
                <div class="d-flex flex-wrap py-1" style="gap: 6px;">
                  <v-chip
                  v-for="chip in optionChips(item)"
                  :key="chip.key"
                  small
                  outlined
                  :color="chip.color">
                    <v-icon small left>{{ chip.icon }}</v-icon>
                    {{ chip.label }}
                  </v-chip>
                  <span v-if="!hasOptions(item)" class="font-italic grey--text">{{ $t('none') }}</span>
                </div>
              </template>

              <template #actions="{ item }">
                <Icon
                icon="mdi-pencil"
                :tooltipText="$t('edit')"
                @icon-clicked="mutate(item, 'edit')"
                :disabled="!isSustainabilityAdmin"
                elementId="due-diligence-exemption-edit"/>
                <Icon icon="mdi-delete-forever"
                :tooltipText="$t('delete')"
                @icon-clicked="mutate(item, 'delete')"
                :disabled="!isSustainabilityAdmin"
                elementId="due-diligence-exemption-delete"/>
              </template>

              <template #custom-controls>
                <Icon
                icon="mdi-refresh"
                :tooltipText="$t('refresh')"
                @icon-clicked="refresh"
                :small="false"/>
                <!-- A disabled v-icon takes no pointer events, so the reason rides on a wrapper -->
                <v-tooltip bottom color="grey" :disabled="endangeredSpeciesEnabled()">
                  <template #activator="{ on }">
                    <span v-on="on">
                      <Icon icon="mdi-plus"
                      :tooltipText="endangeredSpeciesEnabled() ? $t('addDueDiligenceExemption') : undefined"
                      @icon-clicked="createClicked"
                      :small="false"
                      :disabled="!canCreate"
                      elementId="due-diligence-exemption-create"/>
                    </span>
                  </template>
                  <span class="subtitle-1 white--text">{{ $t('addExemptionRequiresEndangeredSpeciesMode') }}</span>
                </v-tooltip>
              </template>

            </DataTable>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { cloneDeep } from 'lodash'
import { mapActions, mapGetters } from 'vuex'
import { UserClaims } from '@/utils/Enumerations.js'
import MitigationHeaders from '@/headers/Mitigation'
import { userAssignedClaim } from '@/utils/ClaimUtility'
import { uniqueDialogId } from '@/utils/componentHelpers'
import { exemptionOptionChips } from '@/utils/dueDiligence/presentation.js'
import { endangeredSpeciesEnabled } from '@/utils/dueDiligence/mode.js'

export default {
  name: 'DueDiligenceExemptionSettings',

  components: {
    ConfirmDelete: () => import('@/components/helper/ConfirmDelete.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    DataTable: () => import('@/components/core/table/DataTable.vue'),
    Dialog: () => import('@/components/Dialog.vue'),
    DueDiligenceExemptionForm: () => import('./DueDiligenceExemptionForm.vue')
  },

  created () {
    this.refresh()
  },

  computed: {
    ...mapGetters('due-diligence-exemption', ['allDueDiligenceExemptions']),

    isSustainabilityAdmin () {
      return userAssignedClaim(UserClaims.SustainabilityAdmin)
    },

    // Existing rows stay visible under None so a mode change doesn't hide configuration
    canCreate () {
      return this.isSustainabilityAdmin && endangeredSpeciesEnabled()
    },

    headers () {
      return MitigationHeaders.dueDiligenceExemptionHeaders()
    },

    customColumns () {
      return [
        {
          slotName: 'description',
          value: 'description'
        },
        {
          slotName: 'options',
          value: 'options'
        },
        {
          slotName: 'actions',
          value: 'actions'
        }
      ]
    }
  },

  data: () => ({
    confirmDeleteDialog: false,
    loading: false,
    dueDiligenceExemption: undefined,
    dialogId: uniqueDialogId('due-diligence-exemption-settings-dialog')
  }),

  methods: {
    ...mapActions('due-diligence-exemption', ['fetchDueDiligenceExemptions', 'deleteDueDiligenceExemption']),
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),

    endangeredSpeciesEnabled,

    optionChips: exemptionOptionChips,

    hasOptions (item) {
      return this.optionChips(item).length > 0
    },

    refresh () {
      this.close()
      this.loading = true
      this.fetchDueDiligenceExemptions().finally(() => {
        this.loading = false
      })
    },

    createClicked () {
      if (!endangeredSpeciesEnabled()) {
        this.setSnackError(this.$t('addExemptionRequiresEndangeredSpeciesMode'))
        return
      }
      this.mutate(null, 'create')
    },

    mutate (dueDiligenceExemption, mutationType) {
      this.dueDiligenceExemption = dueDiligenceExemption ? cloneDeep(dueDiligenceExemption) : undefined
      if (mutationType === 'delete') this.confirmDeleteDialog = true
      this.openOrUpdateDialog(this.dialogId)
    },

    close () {
      this.confirmDeleteDialog = false
      this.closeDialogsAtOrAbove(this.dialogId)
      this.dueDiligenceExemption = undefined
    },

    async performDelete () {
      await this.deleteDueDiligenceExemption(this.dueDiligenceExemption.dueDiligenceExemptionId)
      this.setSnack(this.$t('dueDiligenceExemptionDeleted'))
      this.refresh()
    }
  }
}
</script>

<style scoped>
.exemption-table ::v-deep tbody td {
  vertical-align: top;
  padding-top: 0.5em !important;
  padding-bottom: 0.5em !important;
  line-height: 1.6;
}

.constrained-cell {
  max-width: 35vw;
  overflow-wrap: anywhere;
}
</style>
